import { combineReducers, configureStore, current } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { authReducer, AUTH_FEATURE_KEY } from './auth/auth.slice'
import type { authActions as AuthActions } from './auth/auth.slice'
import { integrationGroupsReducer, INTEGRATION_GROUPS_FEATURE_KEY } from './integration-group/integration-groups.slice'
import { integrationConfigsReducer, INTEGRATION_CONFIGS_FEATURE_KEY } from './integration-config/integration-configs.slice'
import { integrationsReducer, INTEGRATIONS_FEATURE_KEY } from './integration/integrations.slice'
import { serviceAccountsReducer, SERVICE_ACCOUNTS_FEATURE_KEY } from './service-account/service-accounts.slice'
import { sendhubUsersReducer, SENDHUB_USERS_FEATURE_KEY } from './sendhub-users/sendhub-users.slice'
import storage from 'redux-persist/lib/storage'
import {
  createMigrate,
  MigrationManifest,
  PersistConfig,
  PersistedState,
  PersistMigrate,
  persistReducer,
  PersistState,
  persistStore,
} from 'redux-persist'
import { zapierAppsReducer, ZAPIER_APPS_FEATURE_KEY } from './zapier/zapier-apps.slice'
import { zapierTemplatesReducer, ZAPIER_TEMPLATES_FEATURE_KEY } from './zapier/zapier-templates.slice'
import { ZAPIER_ZAPS_FEATURE_KEY, zapierZapsReducer } from './zapier/zapier-zaps.slice'

// const migrations: = {
//   '0': (state: RootState & PersistedState): PersistedState => {
//     // migration clear out device state
//     return {
//       [AUTH_FEATURE_KEY]: { ...state[AUTH_FEATURE_KEY], error: null },
//       [INTEGRATION_GROUPS_FEATURE_KEY]: { ...state[INTEGRATION_GROUPS_FEATURE_KEY], error: null },
//       [INTEGRATIONS_FEATURE_KEY]: { ...state[INTEGRATIONS_FEATURE_KEY], error: null },
//       [INTEGRATION_CONFIGS_FEATURE_KEY]: { ...state[INTEGRATION_CONFIGS_FEATURE_KEY], error: null },
//       [SERVICE_ACCOUNTS_FEATURE_KEY]: { ...state[SERVICE_ACCOUNTS_FEATURE_KEY], error: null },
//       [SENDHUB_USERS_FEATURE_KEY]: { ...state[SENDHUB_USERS_FEATURE_KEY], error: null },
//       [ZAPIER_APPS_FEATURE_KEY]: { ...state[ZAPIER_APPS_FEATURE_KEY], error: null },
//       [ZAPIER_TEMPLATES_FEATURE_KEY]: { ...state[ZAPIER_TEMPLATES_FEATURE_KEY], error: null },
//       [ZAPIER_ZAPS_FEATURE_KEY]: { ...state[ZAPIER_ZAPS_FEATURE_KEY], error: null },
//     }
//   },
//   // 1: (state: RootState) => {
//   //   // migration to keep only device state
//   //   return {
//   //     device: state.device,
//   //   }
//   // },
// }
const LATEST_STORE_VERSION = 1
const clearPersistedStateMigrator: PersistMigrate = async (state, currentVersion) => {
  if (state?._persist.version !== LATEST_STORE_VERSION) return undefined

  const searchParams = new URLSearchParams(window.location.search)
  if (searchParams.has('clearLocalStorage')) return undefined

  return state
}

// const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
//   key: 'root',
//   storage,
//   // transforms: [compressor],
//   version: LATEST_STORE_VERSION,
//   blacklist: [SERVICE_ACCOUNTS_FEATURE_KEY],
//   migrate: clearPersistedStateMigrator,
// }

const BASE_PERSIST_CONFIG = {
  storage,
  blacklist: ['error'],
  version: LATEST_STORE_VERSION,
  migrate: clearPersistedStateMigrator,
}

const authPersistConfig: PersistConfig<ReturnType<typeof authReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: AUTH_FEATURE_KEY,
}
const integrationGroupsPersistConfig: PersistConfig<ReturnType<typeof integrationGroupsReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: INTEGRATION_GROUPS_FEATURE_KEY,
}
const integrationsPersistConfig: PersistConfig<ReturnType<typeof integrationsReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: INTEGRATIONS_FEATURE_KEY,
}
const integrationConfigsPersistConfig: PersistConfig<ReturnType<typeof integrationConfigsReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: INTEGRATION_CONFIGS_FEATURE_KEY,
}
const sendhubUsersPersistConfig: PersistConfig<ReturnType<typeof sendhubUsersReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: SENDHUB_USERS_FEATURE_KEY,
}
// const zapierAppsPersistConfig: PersistConfig<ReturnType<typeof zapierAppsReducer>> = {
//   ...BASE_PERSIST_CONFIG,
//   transforms: [compressor],
//   key: ZAPIER_APPS_FEATURE_KEY,
// }
const zapierTemplatesPersistConfig: PersistConfig<ReturnType<typeof zapierTemplatesReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: ZAPIER_TEMPLATES_FEATURE_KEY,
}
const zapierZapsPersistConfig: PersistConfig<ReturnType<typeof zapierZapsReducer>> = {
  ...BASE_PERSIST_CONFIG,
  key: ZAPIER_ZAPS_FEATURE_KEY,
}

const rootReducer = combineReducers({
  // [AUTH_FEATURE_KEY]: persistReducer(authPersistConfig, authReducer),
  [INTEGRATION_GROUPS_FEATURE_KEY]: persistReducer(integrationGroupsPersistConfig, integrationGroupsReducer),
  [INTEGRATIONS_FEATURE_KEY]: persistReducer(integrationsPersistConfig, integrationsReducer),
  [INTEGRATION_CONFIGS_FEATURE_KEY]: persistReducer(integrationConfigsPersistConfig, integrationConfigsReducer),
  [SERVICE_ACCOUNTS_FEATURE_KEY]: serviceAccountsReducer,
  [SENDHUB_USERS_FEATURE_KEY]: persistReducer(sendhubUsersPersistConfig, sendhubUsersReducer),
  // [ZAPIER_APPS_FEATURE_KEY]: persistReducer(zapierAppsPersistConfig, zapierAppsReducer),
  [ZAPIER_TEMPLATES_FEATURE_KEY]: persistReducer(zapierTemplatesPersistConfig, zapierTemplatesReducer),
  [ZAPIER_ZAPS_FEATURE_KEY]: persistReducer(zapierZapsPersistConfig, zapierZapsReducer),

  [AUTH_FEATURE_KEY]: authReducer,
  // [INTEGRATION_GROUPS_FEATURE_KEY]: integrationGroupsReducer,
  // [INTEGRATIONS_FEATURE_KEY]: integrationsReducer,
  // [INTEGRATION_CONFIGS_FEATURE_KEY]: integrationConfigsReducer,
  // [SERVICE_ACCOUNTS_FEATURE_KEY]: serviceAccountsReducer,
  // [SENDHUB_USERS_FEATURE_KEY]: sendhubUsersReducer,
  [ZAPIER_APPS_FEATURE_KEY]: zapierAppsReducer,
  // [ZAPIER_TEMPLATES_FEATURE_KEY]: zapierTemplatesReducer,
  // [ZAPIER_ZAPS_FEATURE_KEY]: zapierZapsReducer,
})

// const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: rootReducer,
  // Additional middleware can be passed to this array
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env['NODE_ENV'] !== 'production',
  // Optional Redux store enhancers
  enhancers: [],
})

export const persistor = persistStore(store)

declare global {
  interface Window {
    dispatch?: typeof store.dispatch
    store?: typeof store
    toast?: unknown
    state?: unknown
  }
}

window.dispatch = store.dispatch
window.store = store

export type AppDispatch = typeof store.dispatch
export const useAppDispatch: () => AppDispatch = useDispatch // Export a hook that can be reused to resolve types
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export type RootState = ReturnType<typeof store.getState>
